import { z } from "zod";
import { tool } from "@langchain/core/tools";
import type { ChatGoogle } from "@langchain/google";
import {
  AI_CONTRACT_VERSION,
  generateMeaningPackagesResultSchema,
  type ComparisonCandidate,
  type DiscoverMeaningsRequest,
  type DiscoverMeaningsResult,
  type GenerateMeaningPackagesRequest,
  type GenerateMeaningPackagesResult,
  type SelectedSense,
} from "../../../contracts/ai/schemas.ts";
import type { AiCallOptions, VocabularyAi } from "../../../application/ports/ai.ts";
import { ProductError } from "../../../contracts/errors.ts";
import type { CredentialStore } from "../../../application/ports/settings.ts";
import { MIX_WITH_RELATIONS, MIX_WITHOUT_RELATIONS } from "../../../domain/exercises/validate.ts";
import { completeMeaningPackage } from "./assemblePackage.ts";
import { groupedExercisesRequestSchema, packageCardSchema } from "./generationSchemas.ts";
import { discoverModelSchema, mapDiscoverResult } from "./mapDiscover.ts";
import { createGeminiModel } from "./modelFactory.ts";
import {
  DISCOVERY_SYSTEM_PROMPT,
  EXERCISES_SYSTEM_PROMPT,
  PACKAGE_SYSTEM_PROMPT,
  PROMPT_VERSION,
} from "./prompts.ts";
import { translateAiError } from "./errors.ts";

function lookupTool(index: ComparisonCandidate[]) {
  return tool(
    ({ query }: { query: string }) => {
      const needle = query.toLowerCase();
      const matches = index
        .filter((item) =>
          `${item.word} ${item.shortLabel} ${item.definition}`.toLowerCase().includes(needle),
        )
        .slice(0, 8);
      return JSON.stringify(matches);
    },
    {
      name: "lookup_saved_meanings",
      description:
        "Read-only search over the saved-meaning comparison index supplied with this request. Returns matching meaning IDs and labels.",
      schema: z.object({ query: z.string() }),
    },
  );
}

async function invokeStructured<T>(
  model: ChatGoogle,
  schema: z.ZodType<T>,
  messages: Array<{ role: "system" | "user"; content: string }>,
  signal?: AbortSignal,
): Promise<T> {
  const structured = model.withStructuredOutput(schema);
  const raw = await structured.invoke(messages, { signal });
  const parsed = schema.safeParse(raw);
  if (!parsed.success) {
    throw new ProductError("invalid_response", "The model returned an invalid vocabulary payload.");
  }
  return parsed.data;
}

export class LangChainVocabularyAi implements VocabularyAi {
  constructor(private readonly credentials: CredentialStore) {}

  async discoverMeanings(
    request: DiscoverMeaningsRequest,
    options: AiCallOptions,
  ): Promise<DiscoverMeaningsResult> {
    const model = await this.model();
    options.onProgress?.({
      requestId: request.requestId,
      stage: "discovering",
      completedItems: 0,
      totalItems: 1,
      message: "Asking Gemini for useful meanings…",
    });
    try {
      const raw = await invokeStructured(
        model,
        discoverModelSchema,
        [
          { role: "system", content: DISCOVERY_SYSTEM_PROMPT },
          {
            role: "user",
            content: JSON.stringify({
              promptVersion: PROMPT_VERSION,
              request,
            }),
          },
        ],
        options.signal,
      );
      return mapDiscoverResult(raw, request);
    } catch (error) {
      throw translateAiError(error);
    }
  }

  async generateMeaningPackages(
    request: GenerateMeaningPackagesRequest,
    options: AiCallOptions,
  ): Promise<GenerateMeaningPackagesResult> {
    const model = await this.model();
    const outcomes: GenerateMeaningPackagesResult["outcomes"] = [];
    for (const [index, sense] of request.senses.entries()) {
      options.onProgress?.({
        requestId: request.requestId,
        itemId: sense.itemId,
        stage: "generating",
        completedItems: index,
        totalItems: request.senses.length,
        message: `Writing the card for “${sense.shortLabel}”…`,
      });
      try {
        outcomes.push(await this.generateOnePackage(model, request, sense, options, index));
      } catch (error) {
        const translated = translateAiError(error);
        if (translated.code === "cancelled") {
          throw translated;
        }
        outcomes.push({
          itemId: sense.itemId,
          status: "failed",
          code: translated.code,
          message: translated.message,
        });
      }
    }
    const parsed = generateMeaningPackagesResultSchema.safeParse({
      contractVersion: AI_CONTRACT_VERSION,
      requestId: request.requestId,
      outcomes,
    });
    if (!parsed.success) {
      throw new ProductError("invalid_response", "The model returned an invalid vocabulary payload.");
    }
    return parsed.data;
  }

  async testKey(apiKey: string, signal?: AbortSignal): Promise<void> {
    try {
      const model = createGeminiModel(apiKey);
      await model.invoke("Reply with the single word ok.", { signal });
    } catch (error) {
      throw translateAiError(error);
    }
  }

  private async generateOnePackage(
    model: ChatGoogle,
    request: GenerateMeaningPackagesRequest,
    sense: SelectedSense,
    options: AiCallOptions,
    index: number,
  ): Promise<GenerateMeaningPackagesResult["outcomes"][number]> {
    const comparisonTool = lookupTool(request.comparisonIndex);
    const retrieved = await comparisonTool.invoke({
      query: `${sense.shortLabel} ${sense.definition}`,
    });
    const card = await invokeStructured(
      model,
      packageCardSchema,
      [
        { role: "system", content: PACKAGE_SYSTEM_PROMPT },
        {
          role: "user",
          content: JSON.stringify({
            promptVersion: PROMPT_VERSION,
            word: request.word,
            lockedSense: sense,
            comparisonIndex: request.comparisonIndex,
            retrievedComparisons: retrieved,
            existingSenses: request.existingSenses,
          }),
        },
      ],
      options.signal,
    );
    if (card.conflict) {
      return {
        itemId: sense.itemId,
        status: "conflict",
        message: card.conflictMessage?.trim() || "This description cannot be supported. Reselect the meaning.",
      };
    }
    return completeMeaningPackage({
      sense,
      comparisonIndex: request.comparisonIndex,
      card,
      fetchExercises: async ({ relations, issues }) => {
        options.onProgress?.({
          requestId: request.requestId,
          itemId: sense.itemId,
          stage: issues ? "repairing" : "generating",
          completedItems: index,
          totalItems: request.senses.length,
          message: issues
            ? `Repairing practice questions for “${sense.shortLabel}”…`
            : `Writing practice questions for “${sense.shortLabel}”…`,
        });
        const schema = groupedExercisesRequestSchema(relations.length > 0);
        return invokeStructured(
          model,
          schema,
          [
            { role: "system", content: EXERCISES_SYSTEM_PROMPT },
            {
              role: "user",
              content: JSON.stringify({
                promptVersion: PROMPT_VERSION,
                word: request.word,
                lockedSense: sense,
                card: {
                  type: sense.type,
                  shortLabel: card.shortLabel,
                  definition: card.definition,
                  example: card.example,
                  collocations: card.collocations,
                },
                relations,
                requiredCounts: relations.length > 0 ? MIX_WITH_RELATIONS : MIX_WITHOUT_RELATIONS,
                previousIssues: issues,
              }),
            },
          ],
          options.signal,
        );
      },
    });
  }

  private async model() {
    const record = await this.credentials.get();
    if (!record?.apiKey) {
      throw new ProductError("missing_key", "Add a Gemini API key in Settings to generate cards.");
    }
    return createGeminiModel(record.apiKey);
  }
}
